import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useMemo } from 'react';
import { AssetInput } from '../../../../../types/asset/asset.input';
import { ModalPane } from '../../../../../VentoryUI/components/common/Modal';
import { Grid } from '@mui/material';
import DropdownSelect from '../../../Common/DropdownSelect';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { t } from '../../../../../types/translation/Translator';
import { UserContext } from '../../../../../context/UserContext';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomFieldEntityType, CustomFieldType } from '../../../../../types/custom_field/customField';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import { AssetProduct } from '../../../../../types/asset/asset';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { CompanyRole } from '../../../../../types/company_role_assignment';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { DropdownSearchSelect } from '../../../../components/Dropdown';
import { TextInput } from '../../../../components/TextInput';
import { ContactContext } from '../../../../../context/ContactContext';

export enum AssetInfoType {
  create = 'create',
  assign = 'assign',
  return = 'return',
}

interface AssetInfoPaneProps {
  input: AssetInput;
  type: AssetInfoType;
  setInput: (input: AssetInput) => void;
  footer: () => JSX.Element;

  pmdId?: string;
  availableQuantity?: number;
}

export default function AssetInfoPane({
  input,
  setInput,
  footer,
  pmdId,
  type,
  availableQuantity = 0,
}: AssetInfoPaneProps) {
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { contacts } = useContext(ContactContext);
  const { currentCompany } = useContext(CompanyContext);

  const pmdIds = useMemo(() => {
    return [...productMasterData.keys()];
  }, [productMasterData]);

  const userIds = useMemo(() => {
    return [...companyUsers.keys()];
  }, [companyUsers]);

  const relevantCustomFields = useMemo(() => {
    return [...customFields.values()].filter(cf => cf.entityType === CustomFieldEntityType.asset);
  }, [customFields]);

  const productInputDisabled = !!pmdId || type === AssetInfoType.assign || type === AssetInfoType.return;
  const serialInputDisabled = type === AssetInfoType.assign || type === AssetInfoType.return;
  const displayAvailableQuantity = type === AssetInfoType.assign || type === AssetInfoType.return;
  const customFieldInputDisabled =
    (type === AssetInfoType.assign || type === AssetInfoType.return) &&
    !hasCompanyRole(currentCompany.id, CompanyRole.administrator);

  return (
    <ModalPane footer={footer()}>
      <Grid container columnSpacing={1} rowSpacing={1}>
        <Grid item xs={6}>
          <DropdownSelect<string>
            label={t(TranslationKey.assetNumber).singular.label}
            placeholder={t(TranslationKey.selectProductReference).singular.label}
            values={pmdIds}
            disabled={productInputDisabled}
            selectedValue={input.product.productMasterDataId}
            toText={id => {
              return `${productMasterData.get(id)?.productName} ${productMasterData.get(id)?.productNumber}` || '';
            }}
            toElement={id => {
              const item = productMasterData.get(id);
              return (
                <Grid container className='w-full'>
                  <Grid item xs={12} display='flex' className='w-full'>
                    <Grid item>
                      <p className='text-sm font-bold'>{`${item?.productName}`}</p>
                      <p className='text-sm text-gray-500'>{`${item?.productNumber}`}</p>
                    </Grid>
                  </Grid>
                </Grid>
              );
            }}
            onChange={id => {
              setInput(input.withProductMasterDataId(id));
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            disabled={serialInputDisabled}
            label={t(TranslationKey.serial).singular.label}
            placeholder={t(TranslationKey.serial).singular.label}
            value={input.product.serial}
            onChange={serial => setInput(input.withSerial(serial))}
          />
        </Grid>
        <Grid item xs={6}>
          <DropdownSearchSelect
            label={t(TranslationKey.user).singular.label}
            placeholder={t(TranslationKey.selectUser).singular.label}
            items={userIds}
            value={input.location?.entityId}
            textBuilder={id => {
              return companyUsers.get(id)?.email || '';
            }}
            onSelect={id => {
              setInput(input.withUserId(id));
            }}
          />
        </Grid>
        {displayAvailableQuantity ? (
          <Grid item xs={6}>
            <TextInput
              type={'number'}
              disabled={true}
              label={t(TranslationKey.availableQuantity).singular.label}
              placeholder={t(TranslationKey.availableQuantity).singular.label}
              value={availableQuantity.toString()}
              onChange={quantity => {}}
            />
          </Grid>
        ) : null}
        <Grid item xs={6}>
          <TextInput
            type={'number'}
            disabled={!!input.product.serial}
            label={t(TranslationKey.quantity).singular.label}
            placeholder={t(TranslationKey.quantity).singular.label}
            value={input.product.quantity.toString()}
            onChange={quantity => setInput(input.withQuantity(parseFloat(quantity)))}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {relevantCustomFields
              .sort((a, b) => a.index - b.index)
              .map(field => {
                if (field.entityType !== CustomFieldEntityType.product) {
                  return (
                    <Grid item xs={6}>
                      <CustomFieldInput
                        key={field.id}
                        item={field}
                        users={companyUsers}
                        parent={new AssetProduct(input.product).customFields?.get(field.parentId || '')}
                        value={new AssetProduct(input.product).customFields?.get(field.id)?.value || ''}
                        entity={new AssetProduct(input.product)}
                        onChange={(product: AssetProduct) => {
                          relevantCustomFields.forEach(cf => {
                            if (field.id === cf.parentId && cf.id !== cf.parentId) {
                              let prefix = '';
                              const parent = input.product.customFields?.get(cf.parentId);
                              if (parent && parent.type === CustomFieldType.listOfTuples) {
                                prefix = parent.value?.split(':::')[1] || '';
                              }
                              product.withCustomField({ ...cf, value: prefix });
                            }

                            input.product = new AssetProduct(product);
                            setInput(new AssetInput(input));
                          });
                        }}
                        disabled={customFieldInputDisabled}
                        pmdId={input.product.productMasterDataId}
                        contacts={contacts}
                        productMasterData={productMasterData}
                      />
                    </Grid>
                  );
                }
              })}
          </Grid>
        </Grid>
      </Grid>
    </ModalPane>
  );
}
